<template>
  <div v-rect-loading="loading" class="personal-application-page">
    <div class="head">
      <span class="label">
        {{ $t(`${i18nPrefix}.label.${applicationStatus === COMPLETED_APPLICATION ? 'completedApplication' : 'incompleteApplication'}`) }}
      </span>
    </div>
    <TableList
      class="application-list"
      row-key="id"
      rowClassName="application-list-row"
      :listData="showApplicationList"
      :tableColumns="tableColumns"
      noHead
      noAction
      :showCreateBtn="false"
      :pagination="pagination"
      :defaultSort="defaultOrder"
      @pagination-change="handlePaginationChange"
      @sort-change="handleSortChange"
    >
      <template #company="{row}">
        <span class="company" @click="handleClickCompany(row.company.id)">{{ row.companyName }}</span>
      </template>
      <template #job="{row}">
        <span class="job" @click="handleClickJob(row.company.id, row.job.id)">{{ row.jobTitle }}</span>
      </template>
      <template #action="{row}">
        <el-dropdown
          v-if="needToContinueApply(row)"
          placement="bottom-start"
          trigger="click"
          @command="handleDoAction($event, row)"
        >
          <i class="continue-icon iconfont iconapplication"></i>
          <template #dropdown>
            <el-dropdown-menu class="action-dropdown">
              <el-dropdown-item v-if="row.need_profile" command="profile">{{ $t(`${i18nPrefix}.btn.needProfile`) }}</el-dropdown-item>
              <el-dropdown-item v-if="row.need_personality_survey" command="personalitySurvey">{{ $t(`${i18nPrefix}.btn.needPersonalitySurvey`) }}</el-dropdown-item>
              <el-dropdown-item v-if="row.need_problem_solving" command="problemSolving">{{ $t(`${i18nPrefix}.btn.needProblemSolving`) }}</el-dropdown-item>
              <el-dropdown-item v-if="row.need_mini_survey" command="miniSurvey">{{ $t(`${i18nPrefix}.btn.needMiniSurvey`) }}</el-dropdown-item>
              <el-dropdown-item v-if="row.need_invite_survey" command="inviteSurvey">{{ $t(`${i18nPrefix}.btn.needInviteSurvey`) }}</el-dropdown-item>
              <el-dropdown-item v-if="row.need_to_agree_policy" command="agreePolicy">{{ $t(`${i18nPrefix}.btn.needToAgreePolicy`) }}</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <el-dropdown
           v-if="canRevokeApplication(row)"
          placement="bottom-start"
          trigger="click"
          @command="handleRevokeApplication($event, row)"
        >
          <i class="revoke-icon iconfont icondelete"></i>
          <template #dropdown>
            <el-dropdown-menu class="action-dropdown">
              <el-dropdown-item v-if="row.can_revoke_single_application_agreement" command="one">{{ $t(`${i18nPrefix}.btn.revokeThis`) }}</el-dropdown-item>
              <el-dropdown-item v-if="row.can_revoke_agreement" command="all">{{ $t(`${i18nPrefix}.btn.revokeAll`) }}</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </template>
    </TableList>
    <DialogWrapper
        :saveBtnLabelI18nKey="`${i18nPrefix}.btn.yes`"
        :cancelBtnLabelI18nKey="`${i18nPrefix}.btn.no`"
        closeOnCancel
        :title="removeDialog.title"
        :loading="removeDialog.loading"
        :show.sync="removeDialog.show"
        @save="confirmRevokeApplication"
      >
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { COMPLETED_APPLICATION, INCOMPLETE_APPLICATION } from '@/constant/application'
import { ROOT_COMPANY_ID } from '@/constant/company'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'
import mixin from '../../mixin'

export default {
  components: {
    TableList,
    DialogWrapper
  },
  mixins: [mixin],
  data () {
    return {
      COMPLETED_APPLICATION,
      INCOMPLETE_APPLICATION,
      i18nPrefix: 'personal.applications',
      loading: false,
      applicationList: [],
      actionBtn: {},
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      order: '-updated_time',
      removeDialog: {
        show: false,
        loading: false,
        title: null,
        row: null,
        isRevokeAll: false
      }
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.company`),
          dataKey: 'companyName',
          width: '150px',
          slotName: 'company',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.project`),
          width: '200px',
          dataKey: 'jobTitle',
          slotName: 'job',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.action`),
          dataKey: 'action',
          slotName: 'action'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.date`),
          dataKey: 'updated_time',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          showOverflowTooltip: true,
          formatter (data) {
            return moment(data).format('YYYY-MM-DD')
          }
        }
      ]
      return columns
    },
    applicationStatus () {
      return this.$route.meta.applicationStatus
    },
    defaultOrder () {
      const order = this.order
      if (order.includes('-')) {
        return {
          prop: order.split('-')[1],
          order: 'descending'
        }
      } else {
        return {
          prop: order,
          order: 'ascending'
        }
      }
    },
    showApplicationList () {
      const propOrder = this.defaultOrder.prop
      const order = this.defaultOrder.order === 'descending' ? 'desc' : 'asc'
      const pagination = this.pagination
      const startIndex = (pagination.page - 1) * pagination.pageSize
      return _.chain(this.applicationList)
        .orderBy(propOrder, order)
        .slice(startIndex, startIndex + pagination.pageSize)
        .value()
    },
    companyId () {
      return this.$route.query.company_id
    },
    companyCustomMenuList () {
      if (!this.hasCustomTheme) {
        return null
      }
      return _.filter(this.companyThemeSetting.companyDetailSetting.menuList, 'link')
    }
  },
  created () {
    this.initData()
  },
  mounted () {

  },
  methods: {
    ...mapActions('personal', ['getPersonalApplication', 'personalRevokeApplication', 'personalRevokeAllApplication']),
    async initData () {
      this.loading = true
      this.pagination.page = 1
      this.pagination.pageSize = 10
      this.pagination.total = 0
      try {
        await this.loadCandidateApplication()
      } finally {
        this.loading = false
      }
    },
    async loadCandidateApplication () {
      const resp = await this.getPersonalApplication({
        company_id: this.companyId
      })
      if (resp) {
        const pendingJobs = this.formatApplicationData(resp.pending_jobs)
        const finishedJobs = this.formatApplicationData(resp.finished_jobs)
        if (this.applicationStatus === COMPLETED_APPLICATION) {
          this.applicationList = finishedJobs
        } else if (this.applicationStatus === INCOMPLETE_APPLICATION) {
          this.applicationList = pendingJobs
        } else {
          this.applicationList = _.concat(pendingJobs, finishedJobs)
        }
        this.pagination.total = this.applicationList.length
      }
    },
    async handlePaginationChange (pagination) {
      this.pagination = pagination
    },
    handleRevokeApplication (command, row) {
      if (command === 'one') {
        this.removeDialog.title = this.$t(`${this.i18nPrefix}.label.removeOneApplication`, { jobTitle: row.jobTitle })
        this.removeDialog.isRevokeAll = false
      } else if (command === 'all') {
        this.removeDialog.title = this.$t(`${this.i18nPrefix}.label.removeAllApplication`, { companyName: row.companyName })
        this.removeDialog.isRevokeAll = true
      }
      this.removeDialog.show = true
      this.removeDialog.row = row
    },
    async confirmRevokeApplication () {
      this.removeDialog.loading = true
      const row = this.removeDialog.row
      try {
        let resp
        if (this.removeDialog.isRevokeAll) {
          resp = await this.personalRevokeAllApplication({ company_id: row.company.id })
        } else {
          resp = await this.personalRevokeApplication({ jobId: row.job.id })
        }
        if (resp === 'success') {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.revokeSuccess`))
        }
      } finally {
        this.removeDialog.loading = false
        this.removeDialog.show = false
      }
      await this.initData()
      this.$eventBus.$emit('changeHomePageInfo')
    },
    handleDoAction (command, row) {
      if (command === 'profile') {
        this.handleDoProfile(row)
      } else if (command === 'personalitySurvey') {
        this.handleDoPersonalitySurvey(row)
      } else if (command === 'problemSolving') {
        this.handleDoProblemSolving(row)
      } else if (command === 'miniSurvey') {
        this.handleDoMiniSurvey(row)
      } else if (command === 'inviteSurvey') {
        this.handleDoInviteSurvey(row)
      } else if (command === 'agreePolicy') {
        this.handleDoAgreePolicy(row)
      }
    },
    handleClickCompany (companyId) {
      this.$router.push(`/recruiting/company-detail/${companyId}`)
    },
    handleClickJob (companyId, jobId) {
      this.$router.push(`/recruiting/company/${companyId}/job/${jobId}/job-detail`)
    },
    handleDoProfile (row) {
      window.location.href = `/invite-candidate-finish-apply/?jobId=${row.job.id}&edit_resume=1&back=${this.$route.fullPath}`
    },
    handleDoPersonalitySurvey (row) {
      window.location.href = `/invite-candidate-finish-apply/?jobId=${row.job.id}&back=${this.$route.fullPath}`
    },
    handleDoProblemSolving (row) {
      window.location.href = `/invite-candidate-finish-apply/?jobId=${row.job.id}&back=${this.$route.fullPath}`
    },
    handleDoMiniSurvey (row) {
      window.location.href = `/invite-candidate-finish-apply/?jobId=${row.job.id}&back=${this.$route.fullPath}`
    },
    handleDoInviteSurvey (row) {
      window.location.href = `/candidate-invite-job-survey/?jobID=${row.job.id}&back=${this.$route.fullPath}`
    },
    handleDoAgreePolicy (row) {
      window.location.href = `/apply-job-process/${row.job.id}/?back=${this.$route.fullPath}`
    },
    handleSortChange (prop, order) {
      this.order = `${order}${prop}`
      this.pagination.page = 1
    },
    formatApplicationData (data) {
      return _.map(data, item => {
        if (item.company.root_company_id === ROOT_COMPANY_ID.SANOFI) {
          addI18nProperty(item, 'companyName', 'company.root_company_cn_name', 'company.root_company_name')
        } else {
          addI18nProperty(item, 'companyName', 'company.cn_name', 'company.name')
        }
        addI18nProperty(item, 'jobTitle', 'job.cn_title', 'job.title')
        return item
      })
    },
    canRevokeApplication (row) {
      return row.can_revoke_single_application_agreement || row.can_revoke_agreement
    },
    needToContinueApply (row) {
      return row.need_profile ||
              row.need_personality_survey ||
              row.need_problem_solving ||
              row.need_mini_survey ||
              row.need_invite_survey ||
              row.need_to_agree_policy
    }
  },
  watch: {
    $route: {
      handler () {
        this.initData()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.personal-application-page {
  min-height: calc(100vh - var(--header-height, 124px) - 28px);
  min-height: calc(100dvh - var(--header-height, 124px) - 28px);
  background-color: #fff;

  .head {
    display: flex;
    position: sticky;
    top: -14px;
    height: 52px;
    padding: 16px 0 16px 40px;
    z-index: 3;
    background-color: #fff;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.08);

    .label {
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
      color: #606266;
    }
  }

  .application-list {
    padding: 24px 32px 0;

    .company {
      cursor: pointer;
    }

    .job {
      cursor: pointer;
    }

    .continue-icon {
      margin-right: 14px;
      display: block;
      font-size: 18px;

      &:hover {
        color: #0096cc;
        cursor: pointer;
      }
    }

    .revoke-icon {
      display: block;
      font-size: 18px;

      &:hover {
        color: #0096cc;
        cursor: pointer;
      }
    }

    ::v-deep {
      .el-table {
        .el-table__header {
          .el-table__cell {
            padding: 6px 0;
          }
        }
      }
    }
  }

  ::v-deep {
    .application-list-row {
      height: 62px;
    }
  }
}

.action-dropdown {
  padding: 0;

  ::v-deep {
    .popper__arrow {
      display: none;
    }

    .el-dropdown-menu__item {
      padding: 6px 12px;
      line-height: 20px;
    }
  }
}

.el-dropdown-menu.el-popper.action-dropdown {
  margin-top: 4px;
}
</style>
